import invariant from 'invariant';

import { apiIsomorphicFetchUserEnvironment } from '#/api/userEnvironment/apiFetchUserEnvironment';
import { RouteModule } from '#/horizon/components/RouteModule';
import { useEmbedSearchWidget } from '#/modules/embedSearchWidget/contexts/EmbedSearchWidgetContext';
import type { EmbedSearchWidgetLayoutHandleProps } from '#/modules/embedSearchWidget/layout/EmbedSearchWidgetLayoutHandleProps';
import { listingEmbedApiIsomorphicFetchVolop } from '#/modules/embedSearchWidget/listing/volop/api/listingEmbedApiFetchVolop';
import type { embedSearchWidgetViewVolopRoute } from '#/modules/embedSearchWidget/routing/embedSearchWidget.routes';
import type { EmbedSearchWidgetAlgoliaImportedVolopHit } from '#/modules/embedSearchWidget/volops/algolia/EmbedSearchWidgetAlgoliaImportedVolopHit';
import { getSearchAlgoliaDefaultConfig } from '#/modules/search/algolia/searchAlgoliaConfig';
import { searchAlgoliaVolopsIndex } from '#/modules/search/algolia/searchAlgoliaVolopsIndex';
import { SearchVolopsRefinements } from '#/modules/search/volops/components/Refinements/SearchVolopsRefinements';
import { NotFoundPageLoadable } from '#/pages/NotFound/NotFoundPageLoadable';
import type { HorizonAppRouteLoaderArgs } from '#/routing/classes/types/AppRouteLoaderArgs';
import type { HorizonAppRouteComponentProps } from '#/routing/types/HorizonAppRouteComponentProps';
import { idealistNotFound } from '#/routing/utils/idealistNotFound';
import { algoliaGetObject } from '#/utils/algolia/algoliaGetObject';

import { EmbedSearchWidgetViewVolopPageLoadable } from './EmbedSearchWidgetViewVolopPageLoadable';
import { EmbedSearchWidgetViewImportedVolopPageLoadable } from './Imported/EmbedSearchWidgetViewImportedVolopPageLoadable';

export const handle = {
  searchRefinements: <SearchVolopsRefinements includeSourcesFacet />,
  defaultSearchType: 'VOLOP',
  isSearchPage: false,
  searchFromPageType: 'LISTING',
} satisfies EmbedSearchWidgetLayoutHandleProps;

export async function loader({
  request,
  context,
  params: { embedSearchWidgetId, volopId },
}: HorizonAppRouteLoaderArgs<typeof embedSearchWidgetViewVolopRoute>) {
  if (volopId.startsWith('GOLDEN')) {
    const { appConfig } = await apiIsomorphicFetchUserEnvironment({
      request,
      context,
    })();

    const [importedVolopHit] = await Promise.all([
      algoliaGetObject<EmbedSearchWidgetAlgoliaImportedVolopHit>({
        config: getSearchAlgoliaDefaultConfig(),
        index: searchAlgoliaVolopsIndex({ sort: 'relevance' }),
        id: volopId,
        attributes: ['*'],
        isUsingAlgoliaMock: appConfig.algolia.mock,
      }),
      EmbedSearchWidgetViewImportedVolopPageLoadable.load(),
    ]);

    if (!importedVolopHit) return idealistNotFound(null);

    return { type: 'IMPORTED', importedVolopHit } as const;
  }

  try {
    const [volop] = await Promise.all([
      listingEmbedApiIsomorphicFetchVolop({ request, context })(
        embedSearchWidgetId,
        volopId,
      ),
      EmbedSearchWidgetViewVolopPageLoadable.load(),
    ]);

    if (volop.type === 'UNPUBLISHED_VOLOP') return idealistNotFound(null);

    return { type: 'VOLOP', volop } as const;
  } catch {
    return idealistNotFound(null);
  }
}

export default function EmbedSearchWidgetViewVolopHorizonRoute({
  loaderData,
}: HorizonAppRouteComponentProps<typeof loader>) {
  const { embedSearchWidget } = useEmbedSearchWidget();
  invariant(embedSearchWidget, 'Embed search widget not set.');

  switch (loaderData?.type) {
    case undefined:
      return <NotFoundPageLoadable />;
    case 'IMPORTED':
      return (
        <RouteModule title={loaderData.importedVolopHit.name}>
          <EmbedSearchWidgetViewImportedVolopPageLoadable
            importedVolopHit={loaderData.importedVolopHit}
          />
        </RouteModule>
      );
    case 'VOLOP':
      return (
        <RouteModule title={loaderData.volop.name}>
          <EmbedSearchWidgetViewVolopPageLoadable
            volop={loaderData.volop}
            embedSearchWidget={embedSearchWidget}
          />
        </RouteModule>
      );
  }
}
